import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import { api, ApiErrorPayload } from "../api";
import {Game, Theme} from "../../types/games";
import {Language} from "../../translations";
import {setGameSession} from "./gameSession";

export type GameState = {
    loaded: boolean
    error: string | null
    errorCode: string | null
    gameStarted: boolean
    gameTheme?: Theme
    data?: Game
    language: Language
};

const initialState: GameState = {
    loaded: false,
    error: null,
    errorCode: null,
    gameStarted: false,
    gameTheme: undefined,
    data: undefined,
    language: 'fi'
};

export const getGame = createAsyncThunk(
    'game/getGame',
    async (gameSlug:string | undefined, thunkAPI) => {
        try {
            const response = await api.get(`/API/games/slug/${gameSlug}`);
            if (response.session !== null) {
                thunkAPI.dispatch(setGameSession(response.session));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue({message: error.message, code: error.code});
        }
    }
);

const getRejectedError = (payload: unknown, fallback?: string): ApiErrorPayload => {
    if (typeof payload === 'string') {
        return {message: payload};
    }
    if (payload && typeof payload === 'object' && 'message' in payload) {
        const typedPayload = payload as { message?: unknown, code?: unknown };
        if (typeof typedPayload.message === 'string') {
            return {
                message: typedPayload.message,
                code: typeof typedPayload.code === 'string' ? typedPayload.code : undefined
            };
        }
    }
    return {message: fallback || 'Error'};
};


const slice = createSlice({
    name: 'game',
    initialState,
    reducers: {
        setFormData(state, action) {
            if (state.data) {
                state.data.form.forEach((field) => {
                    if (field.name in action.payload) {
                        field.value = action.payload[field.name];
                    }
                });
            }
        },
        startGame(state) {
            state.gameStarted = true;
        },
        updateGameTheme(state, action) {
            state.gameTheme = action.payload;
        }

    },
    extraReducers: (builder) => {
        builder.addCase(getGame.fulfilled, (state, action) => {
            state.loaded = true;
            state.error = null;
            state.errorCode = null;
            state.data = action.payload;
            state.gameTheme = action.payload.theme;
            state.language = action.payload.language;
            if (action.payload.session) {
                state.gameStarted = true;
            }
        });
        builder.addCase(getGame.rejected, (state, action) => {
            state.loaded = true;
            const error = getRejectedError(action.payload, action.error?.message);
            state.error = error.message;
            state.errorCode = error.code || null;
        });
    }
});

export const { startGame, updateGameTheme, setFormData } = slice.actions;
export default slice.reducer;
